import { EntityTypeRepository, EntityInstanceRepository, EntityType } from "../domain/Entities";
import { EntityInstancesViewScreenData, FilterPayload } from "../domain/EntityInstancesViewScreenData";

let _rowIdCounter = 0;
function assignRowIds(items: any[]): any[] {
    for (const item of items) {
        item.__row_id__ = item.__uuid__ || `__tmp_${++_rowIdCounter}`;
    }
    return items;
}

export class EntityInstancesViewComponent {
    typeRepo: EntityTypeRepository;
    instanceRepo: EntityInstanceRepository;
    screen_data: EntityInstancesViewScreenData;
    company_id: number;
    entity_type_uuid: string;

    constructor(
        typeRepo: EntityTypeRepository,
        instanceRepo: EntityInstanceRepository,
        screen_data: EntityInstancesViewScreenData,
        company_id: number = null,
        entity_type_uuid: string = null
    ) {
        this.typeRepo = typeRepo;
        this.instanceRepo = instanceRepo;
        this.screen_data = screen_data;
        this.company_id = company_id;
        this.entity_type_uuid = entity_type_uuid;
    }

    // Context setters
    setCompanyId(company_id: number) {
        this.company_id = company_id;
    }

    setEntityTypeUuid(entity_type_uuid: string) {
        this.entity_type_uuid = entity_type_uuid;
    }

    // Load entity type
    async loadEntityType(): Promise<void> {
        try {
            this.screen_data.entityType = await this.typeRepo.get_type_by_id(this.entity_type_uuid, true);
        } catch (error) {
            this.showError('Failed to load entity type');
            throw error;
        }
    }

    // Load instances with pagination, filtering and sorting
    async loadInstances(): Promise<void> {
        this.screen_data.loading = true;
        try {
            const filters = this.screen_data.filterPayload?.filters || [];
            const limit = this.screen_data.tableOptions.itemsPerPage;
            const offset = (this.screen_data.tableOptions.page - 1) * this.screen_data.tableOptions.itemsPerPage;
            const sortBy = this.screen_data.tableOptions.sortBy.length > 0 ? this.screen_data.tableOptions.sortBy[0] : null;
            const sortDesc = this.screen_data.tableOptions.sortDesc.length > 0 ? this.screen_data.tableOptions.sortDesc[0] : false;

            const result = await this.instanceRepo.filter_instances(
                this.entity_type_uuid,
                this.company_id,
                filters,
                limit,
                offset,
                sortBy,
                sortDesc
            );

            // Clear selection to avoid stale references
            this.screen_data.selectedInstances = [];

            // Handle both old format (array) and new format (object with items and total_count)
            if (Array.isArray(result)) {
                this.screen_data.instances = assignRowIds(result);
                this.screen_data.totalCount = result.length;
            } else if (result && result.items !== undefined) {
                this.screen_data.instances = assignRowIds(result.items);
                this.screen_data.totalCount = result.total_count || result.items.length;
            } else {
                this.screen_data.instances = assignRowIds(result?.instances || []);
                this.screen_data.totalCount = this.screen_data.instances.length;
            }
        } catch (error) {
            this.showError('Failed to load instances');
            throw error;
        } finally {
            this.screen_data.loading = false;
            this.screen_data.paginationLoading = false;
        }
    }

    // Debounced load for pagination changes
    debouncedLoadInstances(): void {
        this.screen_data.paginationWait = true;
        if (!this.screen_data.paginationJustClicked) {
            setTimeout(() => {
                this.waitToLoadInstances();
            }, 300);
            this.screen_data.paginationJustClicked = true;
        }
    }

    private waitToLoadInstances(): void {
        if (!this.screen_data.paginationWait && !this.screen_data.paginationLoading) {
            this.screen_data.paginationJustClicked = false;
            this.screen_data.paginationLoading = true;
            this.loadInstances();
        } else {
            this.screen_data.paginationWait = false;
            setTimeout(() => {
                this.waitToLoadInstances();
            }, 300);
        }
    }

    // Save instance (with dialog close)
    async saveInstance(): Promise<boolean> {
        this.screen_data.saving = true;
        try {
            const uuid = this.screen_data.editingInstance?.__uuid__ || '';
            const isCreation = !this.screen_data.editingInstance;

            const result = await this.instanceRepo.create_or_update_instance(
                uuid,
                this.entity_type_uuid,
                this.screen_data.formPayload,
                this.company_id,
                isCreation
            );

            // Check for error in response
            if (result?.output?.status === 'error' || result?.output?.error) {
                console.error('Save error:', result.output.message || result.output.error);
                this.showError('Error al guardar');
                return false;
            }

            this.showSuccess(this.screen_data.editingInstance ? 'Actualizado' : 'Creado');
            this.closeDialog();
            await this.loadInstances();
            return true;
        } catch (error) {
            this.showError('Error al guardar');
            return false;
        } finally {
            this.screen_data.saving = false;
        }
    }

    // Save instance without closing dialog (for auto-save)
    async saveInstanceWithoutClosing(): Promise<boolean> {
        this.screen_data.saving = true;
        try {
            const uuid = this.screen_data.editingInstance?.__uuid__ || '';
            const isCreation = !this.screen_data.editingInstance;

            const result = await this.instanceRepo.create_or_update_instance(
                uuid,
                this.entity_type_uuid,
                this.screen_data.formPayload,
                this.company_id,
                isCreation
            );

            if (result?.output?.status === 'error' || result?.output?.error) {
                this.showError('Error al guardar');
                return false;
            }

            // Update editingInstance with the new uuid if it was a creation
            if (isCreation && result?.instance_uuid) {
                this.screen_data.editingInstance = {
                    __uuid__: result.instance_uuid,
                    ...this.screen_data.formPayload
                };
            }

            this.showSuccess('Guardado');
            await this.loadInstances();
            return true;
        } catch (error) {
            this.showError('Error al guardar');
            return false;
        } finally {
            this.screen_data.saving = false;
        }
    }

    // Dialog management
    openCreateDialog(): void {
        this.screen_data.editingInstance = null;
        // Use create_default_payload if configured, otherwise empty object
        const defaultPayload = this.screen_data.entityType?.ui_config?.create_default_payload;
        this.screen_data.formPayload = defaultPayload ? { ...defaultPayload } : {};
        this.screen_data.editorCounter++;
        this.screen_data.dialog = true;
    }

    openEditDialog(item: any): void {
        this.screen_data.editingInstance = item;
        const payload: Record<string, any> = {};
        for (const key in item) {
            if (key !== '__row_id__') {
                payload[key] = item[key];
            }
        }
        this.screen_data.formPayload = payload;
        this.screen_data.editorCounter++;
        this.screen_data.dialog = true;
    }

    closeDialog(): void {
        this.screen_data.dialog = false;
        // Clear editing state after transition
        setTimeout(() => {
            this.screen_data.editingInstance = null;
            this.screen_data.formPayload = {};
        }, 200);
    }

    // Filter actions
    handleFilterAction(action_code: string): void {
        if (action_code === 'refresh_list') {
            this.screen_data.tableOptions.page = 1;
            this.loadInstances();
        }
    }

    // Snackbar helpers
    showSuccess(text: string): void {
        this.screen_data.snackbar = { show: true, text, color: 'success' };
    }

    showError(text: string): void {
        this.screen_data.snackbar = { show: true, text, color: 'error' };
    }

    // Initialize component for a given context
    async initialize(company_id: number, entity_type_uuid: string): Promise<void> {
        this.company_id = company_id;
        this.entity_type_uuid = entity_type_uuid;
        await this.loadEntityType();
        await this.loadInstances();
        this.screen_data.initialLoadDone = true;
    }

    // Delete instances (supports single or multiple)
    async deleteInstances(instance_uuids: string[]): Promise<boolean> {
        this.screen_data.deleting = true;
        try {
            const result = await this.instanceRepo.delete_entity_instances(
                instance_uuids,
                this.entity_type_uuid
            );

            // Handle both wrapped (result.output) and direct response formats
            const output = result?.output || result;
            if (output?.status === 'error') {
                this.showError(output?.message || 'Error al eliminar');
                return false;
            }

            const count = instance_uuids.length;
            this.showSuccess(count === 1 ? 'Eliminado' : `${count} eliminados`);
            await this.loadInstances();
            return true;
        } catch (error) {
            this.showError('Error al eliminar');
            return false;
        } finally {
            this.screen_data.deleting = false;
        }
    }

    // Reset for new entity type
    resetForNewEntityType(entity_type_uuid: string): void {
        this.entity_type_uuid = entity_type_uuid;
        this.screen_data.reset();
    }
}
